import React, { useState, Suspense, lazy } from "react";
import { Tooltip } from '@mui/material';
import {
  ICON_LIST,
  CATEGORIES,
  searchIcons
} from "./icons"; // your separate icon file

// Mapping for dynamic imports
const iconLibraries = {
  Io: () => import("react-icons/io5"),
  Md: () => import("react-icons/md"),
  Fa: () => import("react-icons/fa"),
  Ri: () => import("react-icons/ri"),
};

// Helper to dynamically load a single icon safely
const loadIcon = async (libPrefix, iconName) => {
  const lib = await iconLibraries[libPrefix]();
  const Icon = lib[iconName];
  if (!Icon) {
    console.warn(`Icon ${iconName} not found in library ${libPrefix}`);
    // Fallback component if icon not found
    return () => <div style={{ fontSize: 20 }}>❓</div>;
  }
  return Icon;
};

const IconPickerModal = ({ isOpen, onClose, onSelect, category = "All" }) => {
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const iconsPerPage = 60;

  if (!isOpen) return null;

  // Filter icons by search + category
  const filteredIcons = searchIcons(search, CATEGORIES[category]);

  const totalPages = Math.max(1, Math.ceil(filteredIcons.length / iconsPerPage));
  const startIndex = (currentPage - 1) * iconsPerPage;
  const paginatedIcons = filteredIcons.slice(startIndex, startIndex + iconsPerPage);

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <input
            type="text"
            placeholder="Search icon..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            style={styles.input}
          />
          <button style={styles.closeBtn} onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Icons Grid */}
        <div style={styles.grid}>
          {paginatedIcons.map((name) => {
            const libPrefix = name.slice(0, 2); // Fa, Io, Md
            const IconComponent = lazy(() =>
              loadIcon(libPrefix, name).then((icon) => ({ default: icon }))
            );

            return (
              <Suspense key={name} fallback={<div style={styles.iconBox}>...</div>}>
                <Tooltip title={name.slice(2)}>
                  <div
                    style={styles.iconBox}

                    onClick={() => {
                      onSelect({ name, icon: name });
                      onClose();
                    }}
                  >
                    <IconComponent size={22} />
                  </div>
                </Tooltip>
              </Suspense>
            );
          })}
        </div>

        {/* Pagination */}
        <div style={styles.pagination}>
          <button
            style={styles.pageBtn}
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((prev) => prev - 1)}
          >
            Prev
          </button>
          <span style={styles.pageInfo}>
            Page {currentPage} / {totalPages}
          </span>
          <button
            style={styles.pageBtn}
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((prev) => prev + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default IconPickerModal;

// -------------------- Styles --------------------
const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modal: {
    width: "520px",
    maxHeight: "85vh",
    background: "#fff",
    borderRadius: "10px",
    padding: "15px",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    gap: "10px",
    marginBottom: "10px",
  },
  input: {
    flex: 1,
    padding: "8px",
    borderRadius: "6px",
    border: "1px solid #ccc",
  },
  closeBtn: {
    border: "none",
    background: "#eee",
    padding: "6px 10px",
    borderRadius: "6px",
    cursor: "pointer",
    height: "30px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(6, 1fr)",
    gap: "10px",
    overflowY: "auto",
    maxHeight: "55vh",
    padding: "5px",
  },
  iconBox: {
    cursor: "pointer",
    padding: "10px",
    textAlign: "center",
    border: "1px solid #eee",
    borderRadius: "8px",
    transition: "all 0.2s ease",
  },
  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "10px",
  },
  pageBtn: {
    padding: "6px 10px",
    borderRadius: "6px",
    border: "1px solid #ccc",
    background: "#f5f5f5",
    cursor: "pointer",
  },
  pageInfo: {
    fontSize: "14px",
  },
};